// pdfExportJob.js — Async PDF export job + polling helper.
//
// A backend /api/export/pdf/blueprint/start ill. /lead-magnet/start
// endpointjai azonnal visszaadnak egy `job_id`-t, a Playwright PDF
// generálás háttérben fut (60-75s a nagy branded riportok esetén).
// Ez a helper elindítja a job-ot, pollolja a státuszt, és a completed
// állapotban letölti a PDF-et blob-ként.
//
// Használat:
//   const blob = await runPdfExportJob(api, {
//     start: () => api.post("/export/pdf/blueprint/start", { blueprint_type: "client", entry_id, open_sections }),
//     onProgress: ({ status, elapsedMs }) => { ... },  // opcionális
//   });
//   // → blob letölthető pl. URL.createObjectURL(blob) útján.

import { api } from "./api";

/**
 * Elindít egy backend PDF-job-ot, pollolja addig, amíg completed / failed,
 * majd letölti a PDF blob-ot.
 *
 * @param {Object} opts
 * @param {() => Promise<{data: {job_id: string, status: string}}>} opts.start
 *   Az induló POST hívó függvény. A hívónak kell tudnia melyik endpoint kell.
 * @param {(state: {status: string, elapsedMs: number}) => void} [opts.onProgress]
 *   Opcionális callback minden polling ciklus után.
 * @param {number} [opts.pollIntervalMs=1500] Pollolás gyakorisága.
 * @param {number} [opts.maxWaitMs=180000] Maximum várakozási idő (3 perc).
 * @returns {Promise<Blob>} A letöltött PDF blob.
 */
export async function runPdfExportJob({
  start,
  onProgress,
  pollIntervalMs = 1500,
  maxWaitMs = 180000,
} = {}) {
  // 1) Job indítás — ez már kis Playwright-független hívás, kb. <500ms.
  const startRes = await start();
  const jobId = startRes?.data?.job_id;
  if (!jobId) {
    throw new Error("A PDF job indítása nem adott vissza job_id-t.");
  }

  // 2) Pollolás.
  const t0 = Date.now();
  let lastStatus = "queued";
  while (true) {
    const elapsedMs = Date.now() - t0;
    if (elapsedMs > maxWaitMs) {
      throw new Error(
        `A PDF generálás időtúllépés (${Math.round(elapsedMs / 1000)}s). ` +
          "Kérlek próbáld újra, vagy jelezd az adminnak."
      );
    }

    let statusRes;
    try {
      statusRes = await api.get(`/export/pdf/job/${jobId}`);
    } catch (e) {
      const status = e?.response?.status;
      if (status === 404) {
        throw new Error("A PDF job nem található (lehet, hogy lejárt).");
      }
      // Átmeneti hiba — próbáljuk újra egy poll-ciklus után.
      await sleep(pollIntervalMs);
      continue;
    }

    const st = statusRes?.data || {};
    lastStatus = st.status || lastStatus;
    if (typeof onProgress === "function") {
      try {
        onProgress({ status: lastStatus, elapsedMs, jobId });
      } catch (_e) {
        // Progress callback nem törheti meg a poll ciklust.
      }
    }

    if (lastStatus === "completed") {
      // 3) Letöltés — a job dokumentumban tárolt base64 PDF-et adja
      //    vissza a backend byte streamként.
      const dlRes = await api.get(`/export/pdf/job/${jobId}/download`, {
        responseType: "blob",
      });
      return new Blob([dlRes.data], { type: "application/pdf" });
    }
    if (lastStatus === "failed") {
      const errMsg = st.error_message || "Ismeretlen hiba";
      throw new Error(errMsg);
    }
    // "queued" | "running" — vár.
    await sleep(pollIntervalMs);
  }
}

function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Kényelmi wrapper — letölti a blob-ot a felhasználó gépére.
 */
export function downloadBlobAsFile(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename || "download.pdf";
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
